<!--
  MiniQuadra — ported from the React+Babel prototype at
  `design quadra/Mini Quadra.html`. Pure Svelte 5 (runes) + inline SVG.
  No external deps. UI copy in Brazilian Portuguese.
-->
<script lang="ts">
  import type {
    MiniQuadraProps,
    CourtImpact,
    TrailPoint,
    PlayerFilter,
    CourtView,
    CourtSurface,
    CourtStyle,
    CourtMode,
  } from './types.js';
  import { COURT, zonesForHalf, surfaceFill, iso } from './geometry.js';

  const {
    impacts = [],
    trail = [],
    view: initialView = 'top',
    surface: initialSurface = 'grass',
    style: initialStyle = 'neon',
    mode: initialMode = 'impacts',
    player: initialPlayer = 'all',
    showLabels: initialShowLabels = true,
    showTrail = false,
    lastEvent = null,
    score = null,
    players = [],
    compact = false,
    livePlayers,
    liveBall = null,
    calibrationBadge = null,
  }: MiniQuadraProps = $props();

  // Ball-dot opacity driven by tracker confidence. Keep a minimum so very
  // low-confidence predictions still hint a "last known" position without
  // being visually loud.
  const ballAlpha = $derived(
    liveBall ? Math.max(0.15, Math.min(1, liveBall.confidence)) : 0,
  );

  // Interactive local state — seeded from props once; afterwards user-owned.
  // svelte-ignore state_referenced_locally
  let view = $state<CourtView>(initialView);
  // svelte-ignore state_referenced_locally
  let surface = $state<CourtSurface>(initialSurface);
  // svelte-ignore state_referenced_locally
  let styleVar = $state<CourtStyle>(initialStyle);
  // svelte-ignore state_referenced_locally
  let mode = $state<CourtMode>(initialMode);
  // svelte-ignore state_referenced_locally
  let player = $state<PlayerFilter>(initialPlayer);
  // svelte-ignore state_referenced_locally
  let showLabels = $state<boolean>(initialShowLabels);
  let hoverZone = $state<string | null>(null);
  // Force periodic re-render so impact halos fade over time.
  let tick = $state(0);
  $effect(() => {
    const id = setInterval(() => (tick = tick + 1), 120);
    return () => clearInterval(id);
  });

  const topZones = zonesForHalf('top');
  const botZones = zonesForHalf('bottom');
  const allZones = [...topZones, ...botZones];

  const visibleImpacts = $derived.by(() => {
    if (player === 'all') return impacts;
    const pNum = player === 'p1' ? 1 : 2;
    return impacts.filter((i) => i.player === pNum);
  });

  const total = $derived(visibleImpacts.length);

  // Reference `tick` here so the halo-fade animation recomputes every interval.
  const now = $derived.by(() => {
    tick;
    return Date.now();
  });

  const p1Color = $derived(players.find((p) => p.id === 'p1')?.color ?? 'var(--p1)');
  const p2Color = $derived(players.find((p) => p.id === 'p2')?.color ?? 'var(--p2)');
  const p1Name = $derived(players.find((p) => p.id === 'p1')?.name ?? 'P1');
  const p2Name = $derived(players.find((p) => p.id === 'p2')?.name ?? 'P2');

  // --- TopCourt helpers ---
  const pad = 6;
  const topVbW = COURT.DOUBLES_W + pad * 2;
  const topVbH = COURT.L + pad * 2;
  const singlesX = pad + (COURT.DOUBLES_W - COURT.W) / 2;
  const singlesY = pad;
  const gradA = $derived(surfaceFill(surface).a);
  const gradB = $derived(surfaceFill(surface).b);

  // --- IsoCourt bounds ---
  const isoCorners = [
    iso(0, 0),
    iso(COURT.DOUBLES_W, 0),
    iso(COURT.DOUBLES_W, COURT.L),
    iso(0, COURT.L),
  ];
  const isoMinX = Math.min(...isoCorners.map((c) => c.X)) - 4;
  const isoMaxX = Math.max(...isoCorners.map((c) => c.X)) + 4;
  const isoMinY = Math.min(...isoCorners.map((c) => c.Y)) - 6;
  const isoMaxY = Math.max(...isoCorners.map((c) => c.Y)) + 6;
  const isoVbW = isoMaxX - isoMinX;
  const isoVbH = isoMaxY - isoMinY;
  const singlesOff = (COURT.DOUBLES_W - COURT.W) / 2;

  function isoOff(x: number, y: number): { X: number; Y: number } {
    const p = iso(x, y);
    return { X: p.X - isoMinX, Y: p.Y - isoMinY };
  }

  function isoPoly(points: { X: number; Y: number }[]): string {
    return points.map((p) => `${p.X},${p.Y}`).join(' ');
  }

  // Precomputed iso line endpoints (do not depend on reactive state).
  const isoLineA1 = isoOff(singlesOff, 0);
  const isoLineA2 = isoOff(singlesOff, COURT.L);
  const isoLineB1 = isoOff(singlesOff + COURT.W, 0);
  const isoLineB2 = isoOff(singlesOff + COURT.W, COURT.L);
  const isoLineSL1 = isoOff(singlesOff, COURT.L / 2 - COURT.SERVICE_Y);
  const isoLineSL2 = isoOff(singlesOff + COURT.W, COURT.L / 2 - COURT.SERVICE_Y);
  const isoLineSB1 = isoOff(singlesOff, COURT.L / 2 + COURT.SERVICE_Y);
  const isoLineSB2 = isoOff(singlesOff + COURT.W, COURT.L / 2 + COURT.SERVICE_Y);
  const isoLineCT = isoOff(singlesOff + COURT.W / 2, COURT.L / 2 - COURT.SERVICE_Y);
  const isoLineCB = isoOff(singlesOff + COURT.W / 2, COURT.L / 2 + COURT.SERVICE_Y);
  const isoNetA = isoOff(0, COURT.L / 2);
  const isoNetB = isoOff(COURT.DOUBLES_W, COURT.L / 2);
  const isoNetH = 4.5;
  const isoP1 = isoOff(COURT.DOUBLES_W / 2, COURT.L + 4);
  const isoP2 = isoOff(COURT.DOUBLES_W / 2, -4);
  const isoGroundPoly = [
    isoOff(-2, -2),
    isoOff(COURT.DOUBLES_W + 2, -2),
    isoOff(COURT.DOUBLES_W + 2, COURT.L + 2),
    isoOff(-2, COURT.L + 2),
  ];
  const isoSurfacePoly = [
    isoOff(0, 0),
    isoOff(COURT.DOUBLES_W, 0),
    isoOff(COURT.DOUBLES_W, COURT.L),
    isoOff(0, COURT.L),
  ];
  const isoNetPoly = [
    isoNetA,
    isoNetB,
    { X: isoNetB.X, Y: isoNetB.Y - isoNetH },
    { X: isoNetA.X, Y: isoNetA.Y - isoNetH },
  ];
  const isoMeshPoints = Array.from({ length: 18 }).map((_, k) => {
    const t = k / 18;
    return {
      X: isoNetA.X + (isoNetB.X - isoNetA.X) * t,
      Y: isoNetA.Y + (isoNetB.Y - isoNetA.Y) * t,
    };
  });

  function zoneIdForIdx(i: number, id: string): string {
    return `${i < 5 ? 'top-' : 'bot-'}${id}`;
  }

  function impactLife(t: number): number {
    // Retained for parity with the prototype — fade over 5s.
    return Math.max(0, 1 - (now - t) / 5000);
  }

  function impactColor(p: 1 | 2): string {
    return p === 1 ? p1Color : p2Color;
  }

  // Rally trail path for TopCourt
  function topTrailD(t: readonly TrailPoint[]): string {
    return t
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${singlesX + p.x} ${singlesY + p.y}`)
      .join(' ');
  }

  // Rally trail path for IsoCourt with arc through midpoint for "rally feel"
  function isoTrailD(t: readonly TrailPoint[]): string {
    return t
      .map((p, i) => {
        const pp = isoOff(singlesOff + p.x, p.y);
        if (i === 0) return `M ${pp.X} ${pp.Y}`;
        const prev = isoOff(singlesOff + t[i - 1]!.x, t[i - 1]!.y);
        const mid = { X: (pp.X + prev.X) / 2, Y: Math.min(pp.Y, prev.Y) - 3 };
        return `Q ${mid.X} ${mid.Y} ${pp.X} ${pp.Y}`;
      })
      .join(' ');
  }

  function scoreFromPoints(pts: number): string {
    if (pts <= 0) return '0';
    if (pts === 1) return '15';
    if (pts === 2) return '30';
    if (pts === 3) return '40';
    return 'AD';
  }
</script>

<div class="stage" data-style={styleVar}>
  <div class="stage-inner">
    <header class="hdr">
      <div class="hdr-left">
        <div class="logo" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <circle cx="11" cy="11" r="9.5" stroke="var(--g-neon)" stroke-width="1.5" />
            <path d="M 3 6 Q 11 14 19 6" stroke="var(--g-neon)" stroke-width="1" fill="none" />
            <path d="M 3 16 Q 11 8 19 16" stroke="var(--g-neon)" stroke-width="1" fill="none" />
          </svg>
        </div>
        <div>
          <div class="hdr-title">QUADRA<span style:color="var(--g-neon)">·</span>TRACK</div>
          <div class="hdr-sub mono">rastreamento ao vivo / análise por zona</div>
        </div>
      </div>
      <div class="statusbar">
        <div class="status-left">
          <span class="dot" aria-hidden="true"></span>
          <span class="mono live-label">AO VIVO</span>
        </div>
        <div class="status-mid mono">
          {#if lastEvent}
            <span style:color={lastEvent.player === 1 ? p1Color : p2Color}>
              {lastEvent.player === 1 ? p1Name : p2Name}
            </span>
            <span class="sep">·</span>
            <span>{lastEvent.zone}</span>
            <span class="sep">·</span>
            <span>{lastEvent.speed} km/h</span>
          {:else}
            <span style:opacity="0.4">aguardando impacto</span>
          {/if}
        </div>
        {#if score}
          <div class="status-right mono">
            <span style:color={p1Color}>{scoreFromPoints(score.p1)}</span>
            <span class="sep">·</span>
            <span style:color={p2Color}>{scoreFromPoints(score.p2)}</span>
          </div>
        {/if}
      </div>
    </header>

    <div class="layout">
      <div class="court-wrap">
        {#if view === 'top'}
          <svg viewBox={`0 0 ${topVbW} ${topVbH}`} class="court-svg" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="surf" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color={gradA} />
                <stop offset="100%" stop-color={gradB} />
              </linearGradient>
              <pattern id="grid" width="2" height="2" patternUnits="userSpaceOnUse">
                <path d="M 2 0 L 0 0 0 2" fill="none" stroke="rgba(255,255,255,0.025)" stroke-width="0.05" />
              </pattern>
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="0.6" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            <rect x="0" y="0" width={topVbW} height={topVbH} fill="var(--bg-1)" rx="2" />
            <g>
              <rect x={pad} y={pad} width={COURT.DOUBLES_W} height={COURT.L} fill="url(#surf)" />
              <rect x={pad} y={pad} width={COURT.DOUBLES_W} height={COURT.L} fill="url(#grid)" />
            </g>

            <g>
              {#each allZones as z, i}
                {@const zid = zoneIdForIdx(i, z.id)}
                {@const active = hoverZone === zid}
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <rect
                  x={singlesX + z.x}
                  y={singlesY + z.y}
                  width={z.w}
                  height={z.h}
                  fill={active ? 'var(--g-neon)' : 'transparent'}
                  fill-opacity={active ? 0.22 : 0}
                  stroke="var(--g-neon)"
                  stroke-opacity={active ? 0.7 : 0.12}
                  stroke-width={active ? 0.15 : 0.08}
                  stroke-dasharray="0.4 0.4"
                  onmouseenter={() => (hoverZone = zid)}
                  onmouseleave={() => (hoverZone = null)}
                />
              {/each}
            </g>

            <g stroke="rgba(255,255,255,0.85)" stroke-width="0.18" fill="none" stroke-linecap="square">
              <rect x={pad} y={pad} width={COURT.DOUBLES_W} height={COURT.L} />
              <line x1={singlesX} y1={pad} x2={singlesX} y2={pad + COURT.L} />
              <line x1={singlesX + COURT.W} y1={pad} x2={singlesX + COURT.W} y2={pad + COURT.L} />
              <line
                x1={singlesX}
                y1={pad + COURT.L / 2 - COURT.SERVICE_Y}
                x2={singlesX + COURT.W}
                y2={pad + COURT.L / 2 - COURT.SERVICE_Y}
              />
              <line
                x1={singlesX}
                y1={pad + COURT.L / 2 + COURT.SERVICE_Y}
                x2={singlesX + COURT.W}
                y2={pad + COURT.L / 2 + COURT.SERVICE_Y}
              />
              <line
                x1={singlesX + COURT.W / 2}
                y1={pad + COURT.L / 2 - COURT.SERVICE_Y}
                x2={singlesX + COURT.W / 2}
                y2={pad + COURT.L / 2 + COURT.SERVICE_Y}
              />
              <line x1={singlesX + COURT.W / 2} y1={pad} x2={singlesX + COURT.W / 2} y2={pad + 1} />
              <line
                x1={singlesX + COURT.W / 2}
                y1={pad + COURT.L}
                x2={singlesX + COURT.W / 2}
                y2={pad + COURT.L - 1}
              />
            </g>

            <g>
              <line
                x1={pad - 1}
                y1={pad + COURT.L / 2}
                x2={pad + COURT.DOUBLES_W + 1}
                y2={pad + COURT.L / 2}
                stroke="var(--ink-0)"
                stroke-width="0.35"
                stroke-linecap="round"
              />
              <line
                x1={pad - 1}
                y1={pad + COURT.L / 2}
                x2={pad + COURT.DOUBLES_W + 1}
                y2={pad + COURT.L / 2}
                stroke="var(--g-neon)"
                stroke-width="0.8"
                stroke-linecap="round"
                opacity="0.25"
              />
              <circle cx={pad - 0.8} cy={pad + COURT.L / 2} r="0.5" fill="var(--ink-1)" />
              <circle cx={pad + COURT.DOUBLES_W + 0.8} cy={pad + COURT.L / 2} r="0.5" fill="var(--ink-1)" />
            </g>

            {#if showLabels}
              <g class="mono" fill="var(--ink-1)" font-size="1.4" text-anchor="middle" font-family="JetBrains Mono">
                {#each allZones as z}
                  <text
                    x={singlesX + z.x + z.w / 2}
                    y={singlesY + z.y + z.h / 2 + 0.4}
                    opacity="0.45"
                    style:pointer-events="none">{z.label}</text
                  >
                {/each}
              </g>
            {/if}

            {#if showTrail && trail.length > 1}
              <g>
                <path
                  d={topTrailD(trail)}
                  fill="none"
                  stroke="var(--g-neon)"
                  stroke-width="0.18"
                  stroke-opacity="0.6"
                  stroke-linecap="round"
                  stroke-dasharray="0.3 0.3"
                  filter="url(#glow)"
                />
              </g>
            {/if}

            <g>
              {#each visibleImpacts as imp, i (imp.id)}
                {@const life = impactLife(imp.t)}
                {#if life > 0}
                  {@const cx = singlesX + imp.x}
                  {@const cy = singlesY + imp.y}
                  {@const col = impactColor(imp.player)}
                  {@const isLatest = i === visibleImpacts.length - 1}
                  <g>
                    <circle
                      {cx}
                      {cy}
                      r={1.2 + (1 - life) * 2}
                      fill={col}
                      fill-opacity={0.15 * life}
                      filter="url(#glow)"
                    />
                    <circle {cx} {cy} r="0.7" fill={col} fill-opacity={0.6 * life + 0.2} />
                    <circle {cx} {cy} r="0.35" fill="var(--ink-0)" fill-opacity={life} />
                    {#if isLatest}
                      <circle {cx} {cy} r="1.5" fill="none" stroke={col} stroke-width="0.08" opacity="0.8">
                        <animate attributeName="r" from="0.5" to="3" dur="1s" repeatCount="1" />
                        <animate attributeName="opacity" from="0.9" to="0" dur="1s" repeatCount="1" />
                      </circle>
                    {/if}
                  </g>
                {/if}
              {/each}
            </g>

            {#if !livePlayers}
              <g>
                {#if player === 'all' || player === 'p1'}
                  <g>
                    <circle
                      cx={pad + COURT.DOUBLES_W / 2}
                      cy={pad + COURT.L + 3.5}
                      r="1.4"
                      fill={p1Color}
                      filter="url(#glow)"
                    />
                    <text
                      x={pad + COURT.DOUBLES_W / 2}
                      y={pad + COURT.L + 3.9}
                      text-anchor="middle"
                      font-size="1.4"
                      fill="var(--bg-0)"
                      font-weight="700"
                      font-family="JetBrains Mono">P1</text
                    >
                  </g>
                {/if}
                {#if player === 'all' || player === 'p2'}
                  <g>
                    <circle cx={pad + COURT.DOUBLES_W / 2} cy={pad - 3.5} r="1.4" fill={p2Color} filter="url(#glow)" />
                    <text
                      x={pad + COURT.DOUBLES_W / 2}
                      y={pad - 3.1}
                      text-anchor="middle"
                      font-size="1.4"
                      fill="var(--bg-0)"
                      font-weight="700"
                      font-family="JetBrains Mono">P2</text
                    >
                  </g>
                {/if}
              </g>
            {/if}

            {#if livePlayers?.p1}
              <g class="live-dot">
                <circle
                  cx={singlesX + livePlayers.p1.x}
                  cy={singlesY + livePlayers.p1.y}
                  r="2.0"
                  fill={p1Color}
                  fill-opacity="0.25"
                />
                <circle
                  cx={singlesX + livePlayers.p1.x}
                  cy={singlesY + livePlayers.p1.y}
                  r="1.0"
                  fill={p1Color}
                  filter="url(#glow)"
                />
                <text
                  x={singlesX + livePlayers.p1.x}
                  y={singlesY + livePlayers.p1.y + 0.45}
                  text-anchor="middle"
                  font-size="1.1"
                  fill="var(--bg-0)"
                  font-weight="700"
                  font-family="JetBrains Mono">P1</text
                >
              </g>
            {/if}
            {#if livePlayers?.p2}
              <g class="live-dot">
                <circle
                  cx={singlesX + livePlayers.p2.x}
                  cy={singlesY + livePlayers.p2.y}
                  r="2.0"
                  fill={p2Color}
                  fill-opacity="0.25"
                />
                <circle
                  cx={singlesX + livePlayers.p2.x}
                  cy={singlesY + livePlayers.p2.y}
                  r="1.0"
                  fill={p2Color}
                  filter="url(#glow)"
                />
                <text
                  x={singlesX + livePlayers.p2.x}
                  y={singlesY + livePlayers.p2.y + 0.45}
                  text-anchor="middle"
                  font-size="1.1"
                  fill="var(--bg-0)"
                  font-weight="700"
                  font-family="JetBrains Mono">P2</text
                >
              </g>
            {/if}

            {#if liveBall}
              <g class="live-ball" opacity={ballAlpha}>
                {#if liveBall.trail && liveBall.trail.length > 1}
                  {@const tr = liveBall.trail}
                  {#each tr as tp, i}
                    <circle
                      cx={singlesX + tp.x}
                      cy={singlesY + tp.y}
                      r={0.25 + (i / tr.length) * 0.25}
                      fill="#ffffff"
                      fill-opacity={0.15 + (i / tr.length) * 0.6}
                    />
                  {/each}
                {/if}
                <circle
                  cx={singlesX + liveBall.x}
                  cy={singlesY + liveBall.y}
                  r="1.4"
                  fill="#ffffff"
                  fill-opacity="0.25"
                  filter="url(#glow)"
                />
                <circle
                  cx={singlesX + liveBall.x}
                  cy={singlesY + liveBall.y}
                  r="0.7"
                  fill="#ffffff"
                  filter="url(#glow)"
                />
              </g>
            {/if}
          </svg>
        {:else}
          <!-- Isometric variant -->
          <svg viewBox={`0 0 ${isoVbW} ${isoVbH}`} class="court-svg" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="iso-surf" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color={gradA} stop-opacity="0.95" />
                <stop offset="100%" stop-color={gradB} stop-opacity="1" />
              </linearGradient>
              <filter id="iso-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="0.8" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            <rect x="0" y="0" width={isoVbW} height={isoVbH} fill="var(--bg-1)" />

            <polygon points={isoPoly(isoGroundPoly)} fill="var(--bg-2)" opacity="0.6" />
            <polygon points={isoPoly(isoSurfacePoly)} fill="url(#iso-surf)" />

            <g>
              {#each allZones as z, i}
                {@const zid = zoneIdForIdx(i, z.id)}
                {@const active = hoverZone === zid}
                {@const pts = [
                  isoOff(singlesOff + z.x, z.y),
                  isoOff(singlesOff + z.x + z.w, z.y),
                  isoOff(singlesOff + z.x + z.w, z.y + z.h),
                  isoOff(singlesOff + z.x, z.y + z.h),
                ]}
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <polygon
                  points={isoPoly(pts)}
                  fill={active ? 'var(--g-neon)' : 'transparent'}
                  fill-opacity={active ? 0.28 : 0}
                  stroke="var(--g-neon)"
                  stroke-opacity={active ? 0.8 : 0.14}
                  stroke-width={active ? 0.18 : 0.09}
                  stroke-dasharray="0.5 0.5"
                  onmouseenter={() => (hoverZone = zid)}
                  onmouseleave={() => (hoverZone = null)}
                />
              {/each}
            </g>

            <g stroke="rgba(255,255,255,0.85)" stroke-width="0.18" fill="none">
              <polygon points={isoPoly(isoSurfacePoly)} />
              <line x1={isoLineA1.X} y1={isoLineA1.Y} x2={isoLineA2.X} y2={isoLineA2.Y} />
              <line x1={isoLineB1.X} y1={isoLineB1.Y} x2={isoLineB2.X} y2={isoLineB2.Y} />
              <line x1={isoLineSL1.X} y1={isoLineSL1.Y} x2={isoLineSL2.X} y2={isoLineSL2.Y} />
              <line x1={isoLineSB1.X} y1={isoLineSB1.Y} x2={isoLineSB2.X} y2={isoLineSB2.Y} />
              <line x1={isoLineCT.X} y1={isoLineCT.Y} x2={isoLineCB.X} y2={isoLineCB.Y} />
            </g>

            <g>
              <polygon
                points={isoPoly(isoNetPoly)}
                fill="var(--g-shadow)"
                fill-opacity="0.55"
                stroke="var(--ink-1)"
                stroke-width="0.15"
              />
              {#each isoMeshPoints as pa}
                <line x1={pa.X} y1={pa.Y} x2={pa.X} y2={pa.Y - isoNetH} stroke="rgba(230,255,242,0.2)" stroke-width="0.06" />
              {/each}
              <line
                x1={isoNetA.X}
                y1={isoNetA.Y - isoNetH}
                x2={isoNetB.X}
                y2={isoNetB.Y - isoNetH}
                stroke="var(--ink-0)"
                stroke-width="0.25"
              />
            </g>

            {#if showLabels}
              <g class="mono" fill="var(--ink-1)" font-size="1.1" text-anchor="middle" font-family="JetBrains Mono">
                {#each allZones as z}
                  {@const p = isoOff(singlesOff + z.x + z.w / 2, z.y + z.h / 2)}
                  <text x={p.X} y={p.Y + 0.3} opacity="0.45" style:pointer-events="none">{z.label}</text>
                {/each}
              </g>
            {/if}

            {#if showTrail && trail.length > 1}
              <g>
                <path
                  d={isoTrailD(trail)}
                  fill="none"
                  stroke="var(--g-neon)"
                  stroke-width="0.2"
                  stroke-opacity="0.7"
                  stroke-linecap="round"
                  stroke-dasharray="0.4 0.4"
                  filter="url(#iso-glow)"
                />
              </g>
            {/if}

            <g>
              {#each visibleImpacts as imp, i (imp.id)}
                {@const life = impactLife(imp.t)}
                {#if life > 0}
                  {@const p = isoOff(singlesOff + imp.x, imp.y)}
                  {@const col = impactColor(imp.player)}
                  {@const isLatest = i === visibleImpacts.length - 1}
                  <g>
                    <ellipse
                      cx={p.X}
                      cy={p.Y}
                      rx={1.3 + (1 - life) * 1.5}
                      ry={0.6 + (1 - life) * 0.7}
                      fill={col}
                      fill-opacity={0.2 * life}
                      filter="url(#iso-glow)"
                    />
                    <circle cx={p.X} cy={p.Y - 0.3} r="0.6" fill={col} fill-opacity={0.7 * life + 0.3} />
                    <circle cx={p.X} cy={p.Y - 0.3} r="0.3" fill="var(--ink-0)" fill-opacity={life} />
                    {#if isLatest}
                      <circle cx={p.X} cy={p.Y} r="2" fill="none" stroke={col} stroke-width="0.1">
                        <animate attributeName="r" from="0.4" to="3.5" dur="1s" repeatCount="1" />
                        <animate attributeName="opacity" from="0.9" to="0" dur="1s" repeatCount="1" />
                      </circle>
                    {/if}
                  </g>
                {/if}
              {/each}
            </g>

            <g>
              {#if player === 'all' || player === 'p1'}
                <g>
                  <ellipse cx={isoP1.X} cy={isoP1.Y + 0.5} rx="1.2" ry="0.4" fill="rgba(0,0,0,0.5)" />
                  <circle cx={isoP1.X} cy={isoP1.Y - 1.2} r="1.2" fill={p1Color} filter="url(#iso-glow)" />
                  <text
                    x={isoP1.X}
                    y={isoP1.Y - 0.85}
                    text-anchor="middle"
                    font-size="1.1"
                    fill="var(--bg-0)"
                    font-weight="700"
                    font-family="JetBrains Mono">P1</text
                  >
                </g>
              {/if}
              {#if player === 'all' || player === 'p2'}
                <g>
                  <ellipse cx={isoP2.X} cy={isoP2.Y + 0.5} rx="1.2" ry="0.4" fill="rgba(0,0,0,0.5)" />
                  <circle cx={isoP2.X} cy={isoP2.Y - 1.2} r="1.2" fill={p2Color} filter="url(#iso-glow)" />
                  <text
                    x={isoP2.X}
                    y={isoP2.Y - 0.85}
                    text-anchor="middle"
                    font-size="1.1"
                    fill="var(--bg-0)"
                    font-weight="700"
                    font-family="JetBrains Mono">P2</text
                  >
                </g>
              {/if}
            </g>

            {#if liveBall}
              {@const bp = isoOff(singlesOff + liveBall.x, liveBall.y)}
              <g class="live-ball" opacity={ballAlpha}>
                {#if liveBall.trail && liveBall.trail.length > 1}
                  {@const tr = liveBall.trail}
                  {#each tr as tp, i}
                    {@const tpp = isoOff(singlesOff + tp.x, tp.y)}
                    <circle
                      cx={tpp.X}
                      cy={tpp.Y - 0.3}
                      r={0.25 + (i / tr.length) * 0.25}
                      fill="#ffffff"
                      fill-opacity={0.15 + (i / tr.length) * 0.6}
                    />
                  {/each}
                {/if}
                <circle cx={bp.X} cy={bp.Y - 0.3} r="1.2" fill="#ffffff" fill-opacity="0.25" filter="url(#iso-glow)" />
                <circle cx={bp.X} cy={bp.Y - 0.3} r="0.6" fill="#ffffff" filter="url(#iso-glow)" />
              </g>
            {/if}
          </svg>
        {/if}

        <div class="hud hud-tl mono">
          <div class="hud-k">SUPERFÍCIE</div>
          <div class="hud-v">{surface.toUpperCase()}</div>
        </div>
        <div class="hud hud-tr mono">
          <div class="hud-k">VISTA</div>
          <div class="hud-v">{view === 'top' ? 'TOPO' : 'ISOMÉTRICA'}</div>
        </div>
        <div class="hud hud-bl mono">
          <div class="hud-k">MODO</div>
          <div class="hud-v">{mode === 'impacts' ? 'IMPACTOS' : 'RALLY'}</div>
        </div>
        <div class="hud hud-br mono">
          <div class="hud-k">BOLAS</div>
          <div class="hud-v" style:color="var(--g-neon)">{String(total).padStart(3, '0')}</div>
        </div>
        {#if calibrationBadge}
          <div class="hud hud-center-top mono" role="status">
            <div class="hud-k">CALIBRAÇÃO</div>
            <div class="hud-v" style:color="var(--ink-1)">{calibrationBadge}</div>
          </div>
        {/if}
      </div>

      {#if !compact}
        <div class="controls">
          <div class="ctrl-group">
            <div class="ctrl-label mono">VISTA</div>
            <div class="seg">
              <button class="seg-btn" class:active={view === 'top'} onclick={() => (view = 'top')}>Topo</button>
              <button class="seg-btn" class:active={view === 'iso'} onclick={() => (view = 'iso')}>Iso</button>
            </div>
          </div>
          <div class="ctrl-group">
            <div class="ctrl-label mono">SUPERFÍCIE</div>
            <div class="seg">
              <button class="seg-btn" class:active={surface === 'grass'} onclick={() => (surface = 'grass')}>Grama</button>
              <button class="seg-btn" class:active={surface === 'clay'} onclick={() => (surface = 'clay')}>Saibro</button>
              <button class="seg-btn" class:active={surface === 'hard'} onclick={() => (surface = 'hard')}>Rápida</button>
            </div>
          </div>
          <div class="ctrl-group">
            <div class="ctrl-label mono">JOGADOR</div>
            <div class="seg">
              <button class="seg-btn" class:active={player === 'all'} onclick={() => (player = 'all')}>Todos</button>
              <button class="seg-btn" class:active={player === 'p1'} onclick={() => (player = 'p1')}>P1</button>
              <button class="seg-btn" class:active={player === 'p2'} onclick={() => (player = 'p2')}>P2</button>
            </div>
          </div>
          <div class="ctrl-group">
            <div class="ctrl-label mono">ESTILO</div>
            <div class="seg">
              <button class="seg-btn" class:active={styleVar === 'neon'} onclick={() => (styleVar = 'neon')}>Neon</button>
              <button class="seg-btn" class:active={styleVar === 'minimal'} onclick={() => (styleVar = 'minimal')}>Minimal</button>
              <button class="seg-btn" class:active={styleVar === 'broadcast'} onclick={() => (styleVar = 'broadcast')}>Broadcast</button>
            </div>
          </div>
          <div class="ctrl-group">
            <div class="ctrl-label mono">RÓTULOS</div>
            <div class="seg">
              <button class="seg-btn" class:active={showLabels} onclick={() => (showLabels = true)}>On</button>
              <button class="seg-btn" class:active={!showLabels} onclick={() => (showLabels = false)}>Off</button>
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .stage {
    /* Neon (default) */
    --bg-0: #05100c;
    --bg-1: #0a1a14;
    --bg-2: #0f241b;
    --ink-0: #e6fff2;
    --ink-1: #a8d9bf;
    --ink-2: #5a8572;
    --line: rgba(168, 217, 191, 0.12);
    --line-strong: rgba(168, 217, 191, 0.22);

    --g-neon: oklch(0.88 0.24 145);
    --g-bright: oklch(0.78 0.22 148);
    --g-deep: oklch(0.42 0.12 155);
    --g-shadow: oklch(0.18 0.05 160);

    --p1: oklch(0.88 0.24 145);
    --p2: oklch(0.82 0.18 195);

    --grass-a: oklch(0.45 0.12 150);
    --grass-b: oklch(0.38 0.11 152);
    --clay-a: oklch(0.55 0.13 45);
    --clay-b: oklch(0.48 0.12 40);
    --hard-a: oklch(0.40 0.14 230);
    --hard-b: oklch(0.33 0.12 232);

    width: 100%;
    background: linear-gradient(180deg, var(--bg-1) 0%, var(--bg-0) 100%);
    border: 1px solid var(--line-strong);
    border-radius: 20px;
    padding: 18px;
    color: var(--ink-0);
    font-family: system-ui, 'Space Grotesk', sans-serif;
    letter-spacing: -0.01em;
    box-shadow:
      0 40px 100px -40px rgba(0, 0, 0, 0.8),
      0 0 0 1px rgba(255, 255, 255, 0.02) inset,
      0 0 80px -40px var(--g-neon);
    position: relative;
  }
  .stage[data-style='minimal'] {
    --bg-0: #0b0f0d;
    --bg-1: #131a16;
    --bg-2: #1a241f;
    --ink-0: #eef4f0;
    --ink-1: #9ab0a3;
    --ink-2: #5c6e66;
    --g-neon: oklch(0.82 0.12 150);
    --p1: oklch(0.82 0.12 150);
    --p2: oklch(0.80 0.08 200);
  }
  .stage[data-style='broadcast'] {
    --bg-0: #020806;
    --bg-1: #061510;
    --bg-2: #0a2018;
    --ink-0: #ffffff;
    --g-neon: oklch(0.9 0.27 140);
  }

  .stage-inner { display: flex; flex-direction: column; gap: 14px; }
  .mono { font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: 0; }

  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  .hdr-left { display: flex; align-items: center; gap: 12px; }
  .logo {
    width: 40px; height: 40px; display: grid; place-items: center;
    background: var(--bg-2); border-radius: 10px;
    border: 1px solid var(--line-strong);
    box-shadow: 0 0 20px -8px var(--g-neon) inset;
  }
  .hdr-title { font-size: 16px; font-weight: 700; }
  .hdr-sub { font-size: 10px; color: var(--ink-2); margin-top: 2px; letter-spacing: 0.1em; }

  .statusbar {
    display: flex; align-items: center; gap: 14px;
    padding: 6px 12px;
    background: var(--bg-2);
    border: 1px solid var(--line);
    border-radius: 10px;
    font-size: 12px;
  }
  .status-left { display: flex; align-items: center; gap: 6px; }
  .dot {
    display: inline-block; width: 8px; height: 8px; border-radius: 99px;
    background: #ff3b3b; box-shadow: 0 0 8px #ff3b3b;
    animation: dotPulse 1.4s ease-in-out infinite;
  }
  .live-label { color: #ff6b6b; font-weight: 600; font-size: 10px; letter-spacing: 0.15em; }
  .status-mid { color: var(--ink-0); display: flex; gap: 6px; align-items: center; }
  .sep { color: var(--ink-2); }
  .status-right { color: var(--ink-1); font-size: 13px; }

  .layout { display: flex; flex-direction: column; gap: 12px; }

  .court-wrap {
    position: relative;
    background: var(--bg-1);
    border: 1px solid var(--line);
    border-radius: 14px;
    overflow: hidden;
    aspect-ratio: 5 / 4;
    min-height: 320px;
    box-shadow: 0 20px 60px -30px rgba(0,0,0,0.6) inset;
  }
  .court-svg { width: 100%; height: 100%; display: block; }

  .hud {
    position: absolute;
    font-size: 10px;
    letter-spacing: 0.1em;
    line-height: 1.2;
    padding: 6px 9px;
    background: rgba(5,16,12,0.72);
    backdrop-filter: blur(8px);
    border: 1px solid var(--line-strong);
    border-radius: 8px;
  }
  .hud-k { color: var(--ink-2); font-size: 9px; margin-bottom: 2px; }
  .hud-v { color: var(--ink-0); font-size: 12px; font-weight: 600; }
  .hud-tl { top: 10px; left: 10px; }
  .hud-center-top { top: 10px; left: 50%; transform: translateX(-50%); text-align: center; }
  .hud-tr { top: 10px; right: 10px; text-align: right; }
  .hud-bl { bottom: 10px; left: 10px; }
  .hud-br { bottom: 10px; right: 10px; text-align: right; }

  .controls {
    display: flex; gap: 10px; flex-wrap: wrap;
    padding: 10px;
    background: var(--bg-2);
    border: 1px solid var(--line);
    border-radius: 12px;
  }
  .ctrl-group { display: flex; flex-direction: column; gap: 5px; }
  .ctrl-label { font-size: 9px; color: var(--ink-2); letter-spacing: 0.15em; }

  .seg {
    display: flex; background: var(--bg-0);
    border: 1px solid var(--line);
    border-radius: 8px; padding: 3px; gap: 2px;
  }
  .seg-btn {
    padding: 5px 10px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    background: transparent;
    color: var(--ink-1);
    border: none;
    border-radius: 5px;
    cursor: pointer;
  }
  .seg-btn:hover { color: var(--ink-0); }
  .seg-btn.active {
    background: var(--g-neon);
    color: var(--bg-0);
    box-shadow: 0 0 16px -4px var(--g-neon);
    font-weight: 600;
  }

  @keyframes dotPulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.5); opacity: 0.5; }
  }
</style>
